// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/common/backendFrontend/formatVancouverBe.ts

import { TLiteratureContent } from '../../modules/literatures/types';

const MAX_AUTHORS_BEFORE_ET_AL = 6;

const formatAuthors = (authors?: string[]): string => {
  if (!authors?.length) return '';
  if (authors.length <= MAX_AUTHORS_BEFORE_ET_AL) return authors.join(', ');
  return `${authors.slice(0, MAX_AUTHORS_BEFORE_ET_AL).join(', ')}, et al`;
};

const MONTH_ABBREV = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toCitedDate = (raw: TLiteratureContent['accessDate']): string => {
  if (!raw) return '';
  const d = new Date(raw as unknown as string);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getUTCFullYear()} ${MONTH_ABBREV[d.getUTCMonth()]} ${String(d.getUTCDate()).padStart(2, '0')}`;
};

// CrossRef sets url to the doi.org link, which would print the same identifier twice.
const availableFrom = (url?: string, accessDate?: TLiteratureContent['accessDate'], doi?: string): string => {
  if (!url) return '';
  if (doi && url.toLowerCase().includes(doi.toLowerCase())) return '';
  const cited = toCitedDate(accessDate);
  return cited ? `Available from: ${url} [cited ${cited}]` : `Available from: ${url}`;
};

const withPeriod = (s: string): string => {
  const trimmed = s.trim();
  if (!trimmed) return '';
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

const joinSegments = (segs: (string | undefined | null | false)[]): string =>
  segs
    .map((s) => (s ? s.trim() : ''))
    .filter((s) => s !== '')
    .map(withPeriod)
    .join(' ');

const formatYearVolumeIssuePages = (c: TLiteratureContent): string => {
  const year = c.publicationYear != null ? String(c.publicationYear) : '';
  if (!year && !c.volume && !c.issue && !c.pages) return '';
  let r = year;
  if (c.volume) r += (r ? ';' : '') + c.volume;
  if (c.issue) r += `(${c.issue})`;
  if (c.pages) r += (r ? ':' : '') + c.pages;
  return r;
};

// A chapter sits inside a book; a journal article does not. Only the chapter takes the book imprint.
const isChapter = (c: { type?: string }): boolean => c.type === 'book-or-document';

// NLM cites a chapter's book as one unit: "In: GeneReviews. University of Washington; 1993. p. 12-8."
// - semicolon before the year, the chapter's pages after it.
const formatBookImprint = (c: {
  containerTitle?: string;
  publisher?: string;
  publicationYear?: number;
  pages?: string;
}): string => {
  // A whole book has no container - it IS the book, so it takes the imprint without the "In:".
  let imprint = c.containerTitle ? `In: ${c.containerTitle}.` : '';
  if (c.publisher) {
    imprint += `${imprint ? ' ' : ''}${c.publisher}`;
    if (c.publicationYear != null) imprint += `; ${c.publicationYear}`;
  } else if (imprint && c.publicationYear != null) {
    imprint += ` ${c.publicationYear}`;
  }
  if (imprint && c.pages) imprint = `${withPeriod(imprint)} p. ${c.pages}`;
  return imprint;
};

export const formatVancouver = (content: TLiteratureContent, index?: number): string => {
  const prefix = index != null ? `[${index}] ` : '';
  const imprint = isChapter(content) ? formatBookImprint(content) : '';

  const body = joinSegments([
    formatAuthors(content.authors),
    content.title,
    isChapter(content) ? imprint : content.containerTitle,
    // The imprint already carries the book's year, publisher and pages, so the year segment would
    // repeat them. A journal citation has no publisher at all in NLM style - CrossRef fills one anyway.
    !imprint && formatYearVolumeIssuePages(content),
    content.doi && `doi:${content.doi}`,
    content.pmid && `PMID: ${content.pmid}`,
    availableFrom(content.url, content.accessDate, content.doi),
  ]);

  return body ? `${prefix}${body}`.trim() : '';
};
